import * as React from 'react'
import { CastingStore } from "../../../services/stores/casting.store"
import { I18nService } from '../../../services/utils/i18n.service'
import { debounce } from '../../../services/utils/lodash.service'
import { ModalDecoration } from '../../../services/utils/modals.service'
import CastingTooltipContentComponent from '../../plugins/casting/casting-tooltip-content.component'
import CastingHeaderComponent from './casting-header.component'
import CastingStepsDesktopComponent from './casting-steps-desktop.component'
import { getNextGenStore, initEditCasting } from '../../../services/stores/casting-init-edit-store'
import { CastingNewOrEditProps } from '../../../services/json-schemas/serializers/casting-new-or-edit-props'
import { useOnResize } from '../../../hooks/use-on-resize'

type Props = CastingNewOrEditProps

const CastingDebugAnchorComponent = React.lazy(() => import('./debug/casting-debug-anchor.component'))
const i18nScoped = new I18nService(require('./casting-container-desktop.component.yml'))

export default function CastingContainerDesktopComponent (props: Props) {
  // the only purpose of this state is to force a re-render when the store changes
  const [, setData] = React.useState<CastingStore['data']>(null)
  const [store] = React.useState<CastingStore>(() => {
    const store: CastingStore = getNextGenStore(props)
    void initEditCasting(store, props)
    store.changeListeners.push(setData)
    return store
  })

  const { data, data: { focusedInput, renderingMode, requiredEmployees, requiredSupplier } } = store

  const forgetRequiredEmployeesOrSuppliers = React.useCallback(() => {
    // too much side effect, reload the casting...
    const url = new URLSearchParams(window.location.search)
    url.delete('casting[required_employee_ids][]')
    url.delete('casting[required_supplier_id]')
    window.location.href = window.location.pathname + '?' + url.toString()
  }, [])

  const renderedEmployeeOrSupplierRequired = React.useMemo(() => {
    const requiredProviders = requiredEmployees.length ? requiredEmployees : (requiredSupplier ? [requiredSupplier] : [])
    if (!requiredProviders.length || window.currentUser?.type === 'Employee') return null

    return <div className="u-mtm box js-required-employee-or-supplier">
      <em className="icon-info u-txt--M u-mrm"></em>
      { i18nScoped.t('required_employee_or_supplier', { name: requiredProviders.map(_ => _.name).join(', ') }) }
      <a onClick={ forgetRequiredEmployeesOrSuppliers }>{ i18nScoped.t('click_here') }</a>
    </div>
  }, [forgetRequiredEmployeesOrSuppliers, requiredEmployees, requiredSupplier])

  useOnResize(() => debounce(() => store.setDesktopContainerHeight(), 400, true)(), [store])

  return <div className="containerPage--secondary casting-outer">
    <div className="casting-mainContainer">
      <CastingHeaderComponent store={ store } data={ data } mobile={ false } />

      <div className="containerPage-inner">
        { renderedEmployeeOrSupplierRequired }
        <CastingStepsDesktopComponent store={ store } data={ data } />
      </div>

      { focusedInput === 'price' && <ModalDecoration
        title={ store.getStepTitle('price') }
        contentGenerator={ () => <div className="modal-inner">
          <CastingTooltipContentComponent store={ store } data={ data } focusableInput="price" />
        </div> }
        controls={{ onCancel: () => store.set({ focusedInput: null }), onResult: () => store.set({ focusedInput: null }) }}
        options={{}} /> }
    </div>

    { renderingMode === 'desktop' && window.currentUser?.type === 'Operator' && <React.Suspense fallback={ null }>
      <CastingDebugAnchorComponent store={ store } data={ data } />
    </React.Suspense> }
  </div>
}
